/**
 * ActionPreviewEvent — renderiza tipo 'action_preview'
 * payload: { action_id, resumen, diff, reversibilidad, requiere_aprobacion }
 *
 * R-PT-11: muestra preview/diff y captura la aprobación EXPLÍCITA del usuario.
 *          Pórtico NO infiere ni presupone la aprobación.
 *          La aprobación va a POST /v1/actions/{id}/approve (Bearer, sin body).
 *
 * Máquina de estados (SPEC #280 §B.3): propuesta → aprobando → aprobada
 * | descartada (client-only, §B.5, sin llamada al kernel) | error (§B.4).
 * `diff` es tolerante: hoy llega como string (mock) u objeto {antes,despues}
 * (as-built kernel); la forma definitiva la cierra Cassie (#281) — no se
 * asume ninguna de las dos.
 */

import { useState } from 'react'
import { Wrench, RotateCcw, OctagonAlert } from 'lucide-react'
import { approveAction } from '../../api/client.js'
import { useSession } from '../../context/SessionContext.jsx'

const REVERSIBILIDAD_LABELS = {
  reversible:   { label: 'Reversible',   cls: 'rev-badge--safe',    Icon: RotateCcw },
  irreversible: { label: 'Irreversible', cls: 'rev-badge--danger',  Icon: OctagonAlert },
}

// SPEC §B.4 — mapa de errores del kernel (envelope {error:{code,message}}) a
// mensaje de UI. `retryable`: si es recuperable, la tarjeta vuelve a
// "propuesta" (re-habilita Aprobar); si no, queda en estado terminal "error".
const ERROR_MAP = {
  forbidden:              { message: 'No tienes permiso para aprobar esta acción.', retryable: false },
  accion_no_encontrada:   { message: 'La acción ya no está disponible (expiró o no existe).', retryable: false },
  en_curso:               { message: 'La acción se está ejecutando…', retryable: false },
  accion_estado_terminal: { message: 'La acción ya finalizó o expiró.', retryable: false },
}

function errorInfoFor(err) {
  const known = ERROR_MAP[err?.code]
  if (known) return known
  return { message: err?.message || 'No se pudo aprobar la acción.', retryable: true }
}

// Deriva líneas +/- del diff sin asumir su forma (§B.6, pendiente Cassie #281):
// string -> se pinta tal cual; objeto {antes,despues} -> se derivan líneas.
function DiffBlock({ diff }) {
  if (diff == null) return null

  if (typeof diff === 'string') {
    const lines = diff.split('\n').map((line, i) => {
      const cls = line.startsWith('+') ? 'action-preview__diff-line--add'
        : line.startsWith('-') ? 'action-preview__diff-line--del' : undefined
      return <span key={i} className={cls}>{line}{'\n'}</span>
    })
    return <pre className="action-preview__diff">{lines}</pre>
  }

  if (typeof diff === 'object' && ('antes' in diff || 'despues' in diff)) {
    const antes = diff.antes
    const despues = diff.despues
    const lines = []
    const fmt = v => (typeof v === 'object' && v !== null) ? JSON.stringify(v) : String(v ?? '')

    if (antes && typeof antes === 'object' && despues && typeof despues === 'object') {
      const keys = [...new Set([...Object.keys(antes), ...Object.keys(despues)])]
      for (const key of keys) {
        const before = antes[key]
        const after = despues[key]
        if (JSON.stringify(before) === JSON.stringify(after)) {
          lines.push({ text: `  ${key}: ${fmt(before)}` })
        } else {
          if (before !== undefined) lines.push({ text: `- ${key}: ${fmt(before)}`, cls: 'action-preview__diff-line--del' })
          if (after !== undefined) lines.push({ text: `+ ${key}: ${fmt(after)}`, cls: 'action-preview__diff-line--add' })
        }
      }
    } else {
      if (antes !== undefined) lines.push({ text: `- ${fmt(antes)}`, cls: 'action-preview__diff-line--del' })
      if (despues !== undefined) lines.push({ text: `+ ${fmt(despues)}`, cls: 'action-preview__diff-line--add' })
    }

    return (
      <pre className="action-preview__diff">
        {lines.map((l, i) => <span key={i} className={l.cls}>{l.text}{'\n'}</span>)}
      </pre>
    )
  }

  // Forma desconocida: no se inventa estructura, se pinta cruda.
  return <pre className="action-preview__diff">{JSON.stringify(diff, null, 2)}</pre>
}

export default function ActionPreviewEvent({ payload }) {
  const { action_id, resumen, diff, reversibilidad, requiere_aprobacion } = payload
  const { capabilities } = useSession()
  const [status, setStatus]     = useState('propuesta') // propuesta | aprobando | aprobada | descartada | error
  const [resultado, setResultado] = useState(null)
  const [errorMsg, setErrorMsg] = useState(null)

  const revMeta = REVERSIBILIDAD_LABELS[reversibilidad] ?? {
    label: reversibilidad, cls: 'rev-badge--unknown', Icon: null,
  }
  const RevIcon = revMeta.Icon

  // Gate de capacidad (§B.2): el botón Aprobar solo se pinta si el descriptor
  // concede `aprobar_accion` (DKtec sí; Koana no). El Kernel revalida IAM
  // igualmente (R-PT-04); esto es solo evitar pintar un botón inútil.
  const puedeAprobar = capabilities?.acciones_permitidas?.includes('aprobar_accion') ?? false

  async function handleApprove() {
    setStatus('aprobando')
    setErrorMsg(null)
    try {
      // approveAction ya añade el Bearer en el cliente; no se le pasa token
      // ni ningún payload de negocio (§B.2: acción ya validada por el Kernel).
      const proyeccion = await approveAction(action_id)
      // Reintento idempotente: aprobar dos veces devuelve 200 con el
      // resultado ya registrado (no re-ejecuta) — se trata igual que un OK.
      setResultado(proyeccion?.resultado ?? null)
      setStatus('aprobada')
    } catch (err) {
      const info = errorInfoFor(err)
      setErrorMsg(info.message)
      setStatus(info.retryable ? 'propuesta' : 'error')
    }
  }

  // Descartar es MVP client-only (§B.5): sin llamada al Kernel. La acción
  // pendiente expira sola en el Kernel a los 15 min y no puede ejecutarse
  // sin aprobación.
  function handleDiscard() {
    setStatus('descartada')
  }

  return (
    <div className={'event-action-preview action-preview--' + status}>
      <div className="action-preview__header">
        <span className="action-preview__icon"><Wrench size={16} strokeWidth={1.8} aria-hidden="true" /></span>
        <span className="action-preview__title">Acción propuesta</span>
        <span className={'rev-badge ' + revMeta.cls}>
          {RevIcon && <RevIcon size={13} strokeWidth={2} aria-hidden="true" />}{revMeta.label}
        </span>
      </div>

      <p className="action-preview__resumen">{resumen}</p>

      <DiffBlock diff={diff} />

      {status === 'propuesta' && requiere_aprobacion && (
        <div className="action-preview__actions">
          {puedeAprobar ? (
            <>
              <p className="action-preview__notice">
                Esta acción requiere tu aprobación explícita antes de ejecutarse.
              </p>
              <div className="action-preview__btns">
                <button
                  className="action-btn action-btn--approve"
                  onClick={handleApprove}
                >
                  ✓ Aprobar
                </button>
                <button
                  className="action-btn action-btn--reject"
                  onClick={handleDiscard}
                >
                  ✕ Descartar
                </button>
              </div>
            </>
          ) : (
            <p className="action-preview__notice">
              Esta acción requiere aprobación de alguien con permiso para ello.
            </p>
          )}
          {errorMsg && <p className="action-preview__error">{errorMsg}</p>}
        </div>
      )}

      {status === 'aprobando' && (
        <p className="action-preview__status">Aprobando…</p>
      )}

      {status === 'aprobada' && (
        <p className="action-preview__status action-preview__status--ok">
          ✓ Aprobada — hecho{resultado ? `: ${resultado}` : ''}
        </p>
      )}

      {status === 'descartada' && (
        <p className="action-preview__status action-preview__status--descartada">
          Descartada — la acción no se ejecutará.
        </p>
      )}

      {status === 'error' && (
        <p className="action-preview__status action-preview__status--error">
          {errorMsg}
        </p>
      )}
    </div>
  )
}
